import { createRef, PureComponent } from "react";

import { throttle } from "~/utils/helpers";
import { logger } from "~/utils/logging";

import { MediaPlayerViewMode } from "../media/types";
import { initialState, reducer } from "../media/state";
import { getMediaElementProperties, load } from "../media/helpers";
import {
  CAN_PLAY_THROUGH,
  CAN_PLAY,
  CAPTION_CHANGE,
  END,
  LOAD_START,
  LOADED_DATA,
  LOADED_META_DATA,
  PAUSE,
  PLAY,
  PLAYING,
  PROGRESS_CHANGE,
  SEEKED,
  TIME_UPDATE,
  VOLUME_CHANGE,
  WAITING,
  CAPTION_TEXT_CHANGE,
  RATE_CHANGE,
  AIRPLAYING_CHANGE,
  CAN_AIRPLAY,
  VIEWMODE_CHANGE,
  DURATION_CHANGE,
  SEEKING,
  SEEKING_TIME,
  PlayState,
} from "../media/constants";
import { Track } from "../media/Track";
import { initialValue, MediaPlayerContext } from "../media/context";
import { AudioElement } from "./AudioElement";
import { AudioControls } from "./AudioControls";
import { AudioCaptions } from "./AudioCaptions";

import "./Audio.css";

import type {
  MediaCaptionConfig,
  MediaChapter,
  MediaPlayerAction,
} from "../media/types";
import type { MediaPlayerState, MediaElementState } from "../media/state";
import type { MediaPlayerContextValue } from "../media/context";
import type { CaptionsProp } from "../media/Track";
import type { ReactNode, MutableRefObject } from "react";

// Don't seek past duration - margin so that the session ends instead of replaying.
const MAX_SEEK_THRESHOLD = 0.1;

export interface AudioProps {
  autoplay?: boolean;
  chapters: MediaChapter[] | null;
  children?: ReactNode;
  contextRef?: MutableRefObject<MediaPlayerContextValue | null>;
  disableCollapse?: boolean;
  isDesktopLayout?: boolean;
  src: string;
  startTime?: number;
  playbackRate?: number;
  volume?: number;
  captions?: CaptionsProp[];
  captionsEnabled?: boolean;
  title: string;
  onAirplayingChange?: (airplaying: boolean) => void;
  onCaptionChange?: (enabled: boolean) => void;
  onEnded?: () => void;
  onPaused?: () => void;
  onPlaying?: () => void;
  onPlayStateChange?: (playState: PlayState) => void;
  onRateChange?: (playbackRate: number) => void;
  onReplay?: () => void;
  onVolumeChange?: (volume: number) => void;
  onTimeUpdate?: (timestamp: number) => void;
  onViewModeChange?: (viewMode: MediaPlayerViewMode) => void;
}

export interface AudioPlayerState extends MediaPlayerState {
  isExpanded: boolean;
}

export class AudioPlayer extends PureComponent<AudioProps, AudioPlayerState> {
  mediaElementRef = createRef<HTMLAudioElement>();

  state: AudioPlayerState = {
    ...initialState,
    isExpanded: true,
    captionsEnabled: this.props.captionsEnabled ?? initialState.captionsEnabled,
  };

  static defaultProps = {
    startTime: 0,
    playbackRate: 1,
    chapters: null,
  };

  get media() {
    return this.mediaElementRef.current;
  }

  get mediaElementProperties() {
    if (!this.media) {
      return {} as MediaElementState;
    }

    return getMediaElementProperties(this.media);
  }

  get playState() {
    const { currentTime, duration } = this.mediaElementProperties;
    return duration && currentTime >= duration - 1
      ? PlayState.ENDED
      : PlayState.PLAYING_SESSION;
  }

  componentDidMount() {
    this.load();

    this.handleWindowResize();

    window.addEventListener("resize", this.handleWindowResize);

    this.media?.addEventListener(
      "webkitplaybacktargetavailabilitychanged",
      this.handlePlaybackTargetAvailabilityChanged,
    );

    this.media?.addEventListener(
      "webkitcurrentplaybacktargetiswirelesschanged",
      this.handlePlaybackTargetIsWirelessChanged,
    );
  }

  componentDidUpdate(prevProps: AudioProps, prevState: MediaPlayerState) {
    // src change
    if (prevProps.src != this.props.src) {
      this.load();
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleWindowResize);

    this.media?.removeEventListener(
      "webkitplaybacktargetavailabilitychanged",
      this.handlePlaybackTargetAvailabilityChanged,
    );

    this.media?.removeEventListener(
      "webkitcurrentplaybacktargetiswireless",
      this.handlePlaybackTargetIsWirelessChanged,
    );
  }

  ////////////////////////////
  // General functionality //
  ///////////////////////////

  dispatch = (action: Partial<MediaPlayerAction>) => {
    this.setState((state: MediaPlayerState) =>
      reducer(state, {
        ...action,
        mediaElementProperties: this.mediaElementProperties,
      }),
    );

    // Side effects
    switch (action.type) {
      case VIEWMODE_CHANGE:
        this.props.onViewModeChange?.(
          action.payload || MediaPlayerViewMode.inline,
        );
        break;
      case TIME_UPDATE:
        this.props.onTimeUpdate?.(this.mediaElementProperties.currentTime);
        this.props.onPlayStateChange?.(this.playState);
        break;
      case RATE_CHANGE:
        this.props.onRateChange?.(this.mediaElementProperties.playbackRate);
        break;
      case VOLUME_CHANGE:
        this.props.onVolumeChange?.(this.mediaElementProperties.volume);
        break;
      case PAUSE:
        this.props.onPaused?.();
        break;
      case PLAYING:
        this.props.onPlaying?.();
        break;
      case CAPTION_CHANGE:
        this.props.onCaptionChange?.(action.payload || false);
        break;
      case AIRPLAYING_CHANGE:
        this.props.onAirplayingChange?.(action.payload || false);
        break;
      case END:
        this.props.onPlayStateChange?.(PlayState.ENDED);
        break;
      default:
        break;
    }
  };

  load = () => {
    if (this.mediaElementRef.current) {
      load(this.mediaElementRef.current, this.props);
    }
  };

  loadMediaState = () => {
    // Seek to start time
    this.media!.currentTime = Math.min(
      this.media!.duration - MAX_SEEK_THRESHOLD,
      this.props.startTime ?? 0,
    );

    // Set playback rate
    this.media!.playbackRate = this.props.playbackRate ?? 1;

    // Set volume
    this.media!.volume = this.props.volume ?? 1;

    // Autoplay
    if (this.props.autoplay) {
      this.play();
    } else {
      this.pause();
    }

    // Set caption visibility
    const track = this.media?.textTracks[0];
    if (track) {
      track.mode = this.state.captionsEnabled ? "showing" : "disabled";
    }

    if (
      this.media?.duration &&
      (this.props.startTime ?? 0) + 1 > this.media.duration
    ) {
      this.play();
    }
  };

  play = () => {
    const promise = this.media?.play?.();

    if (promise !== undefined) {
      promise
        .then(() => {
          this.dispatch({ type: PLAY });
        })
        .catch(() => {
          // TODO do we need to autoplay audio-only? How to handle unmuting
        });
    }
  };

  pause = () => {
    try {
      this.media?.pause();
      this.dispatch({ type: PAUSE });
    } catch (error) {}
  };

  scrub = () => {
    this.pause();
  };

  seek = (time: number) => {
    if (!this.state.hasStarted) return;
    try {
      this.media!.currentTime = Math.max(
        0,
        Math.min(this.media!.duration - MAX_SEEK_THRESHOLD, time),
      );
      this.play();
      this.dispatch({ type: SEEKED });
    } catch (error) {
      logger.error(`Audio player "seek" failed`, error);
    }
  };

  // TODO accept a lang config to select to corresponding track
  setCaptions = (config?: MediaCaptionConfig) => {
    this.dispatch({
      type: CAPTION_CHANGE,
      payload: Boolean(config),
    });
  };

  setPlaybackRate = (playbackRate: number) => {
    try {
      this.media!.playbackRate = playbackRate;
      this.dispatch({ type: RATE_CHANGE, payload: playbackRate });
    } catch (error) {
      logger.error(`Audio player "setPlaybackRate" failed`, error);
    }
  };

  setVolume = (volume: number) => {
    try {
      this.media!.volume = volume;
      this.dispatch({ type: VOLUME_CHANGE, payload: volume });
    } catch (error) {
      logger.error(`Audio player "setVolume" failed`, error);
    }
  };

  setSeeking = (seeking: boolean) => {
    this.dispatch({
      type: SEEKING,
      payload: seeking,
    });
  };

  setSeekingTime = (seekingTime: number) => {
    this.dispatch({
      type: SEEKING_TIME,
      payload: seekingTime,
    });
  };

  setAirplaying(airplaying: boolean) {
    this.dispatch({
      type: AIRPLAYING_CHANGE,
      payload: airplaying,
    });
  }

  setCanAirplay(canAirplay: boolean) {
    this.dispatch({
      type: CAN_AIRPLAY,
      payload: canAirplay,
    });
  }

  handleAirplayControlClick = () => {
    try {
      (this.mediaElementRef.current as any)?.webkitShowPlaybackTargetPicker();
    } catch (error) {}
  };

  handleReplayControlClick = () => {
    this.props.onReplay?.();
  };

  handleExpandedChange = () => {
    this.setState(({ isExpanded }) => ({ isExpanded: !isExpanded }));
  };

  //////////////////////////////
  // Container event handlers //
  //////////////////////////////

  handleWindowResize = () => {
    const isLandscape = window.matchMedia("(orientation:landscape)").matches;
    if (isLandscape) this.setState(() => ({ isExpanded: false }));
  };

  /////////////////////////////////////
  // HTMLMediaElement event handlers //
  /////////////////////////////////////

  handleLoadStart = () => {
    this.dispatch({ type: LOAD_START });
  };

  handleDurationChange = () => {
    this.dispatch({ type: DURATION_CHANGE });
    this.loadMediaState();
  };

  handleWaiting = () => {
    this.dispatch({ type: WAITING });
  };

  handleCanPlay = () => {
    this.dispatch({ type: CAN_PLAY });
  };

  handleCanPlayThrough = () => {
    this.dispatch({ type: CAN_PLAY_THROUGH });
  };

  handlePlaying = () => {
    this.dispatch({ type: PLAYING });
  };

  handleProgress = () => {
    this.dispatch({ type: PROGRESS_CHANGE });
  };

  handleEnded = () => {
    if (!this.media?.duration) return;
    this.dispatch({ type: END });
    this.props.onEnded?.();
  };

  handleLoadedMetaData = () => {
    this.dispatch({ type: LOADED_META_DATA });
  };

  handleLoadedData = () => {
    this.dispatch({ type: LOADED_DATA });
  };

  handleTimeUpdate = throttle(() => {
    if (!this.state.hasStarted) return;
    this.dispatch({ type: TIME_UPDATE });
  }, 250);

  handleVolumeChange = () => {
    this.dispatch({ type: VOLUME_CHANGE });
  };

  handlePause = () => {
    this.dispatch({ type: PAUSE });
  };

  handleTrackCueChange = (text: string) => {
    this.dispatch({ type: CAPTION_TEXT_CHANGE, payload: text });
  };

  handlePlaybackTargetAvailabilityChanged = (event: any) => {
    this.setCanAirplay(event.availability === "available");
  };

  handlePlaybackTargetIsWirelessChanged = () => {
    const isAirplaying =
      (this.media as any)?.webkitCurrentPlaybackTargetIsWireless || false;
    this.setAirplaying(isAirplaying);
  };

  ///////////////
  // Rendering //
  ///////////////

  render() {
    const { chapters, captions, children, contextRef, disableCollapse, title } =
      this.props;

    const context: MediaPlayerContextValue = {
      ...this.state,
      chapters: chapters,
      mediaElement: this.mediaElementRef.current,
      pause: this.pause,
      play: this.play,
      scrub: this.scrub,
      seek: this.seek,
      setCaptions: this.setCaptions,
      setPlaybackRate: this.setPlaybackRate,
      setVolume: this.setVolume,
      setViewMode: initialValue.setViewMode,
      setViewModeFullscreenSlides: initialValue.setViewModeFullscreenSlides,
      setViewModeFullscreenWindowPosition: initialValue.setViewMode,
      setSeeking: this.setSeeking,
      setSeekingTime: this.setSeekingTime,
    };

    if (contextRef) {
      contextRef.current = context;
    }

    const isExpanded = this.props.isDesktopLayout || this.state.isExpanded;
    const onExpandedChange =
      !disableCollapse && !this.props.isDesktopLayout
        ? this.handleExpandedChange
        : undefined;

    return (
      <MediaPlayerContext.Provider value={context}>
        {!this.props.isDesktopLayout && <AudioCaptions />}
        <div
          data-desktop-mode={this.props.isDesktopLayout}
          aria-expanded={isExpanded}
          className="AudioPlayer"
        >
          <AudioControls
            isExpanded={isExpanded}
            isDesktopLayout={this.props.isDesktopLayout}
            onAirplayControlClick={this.handleAirplayControlClick}
            onReplayControlClick={this.handleReplayControlClick}
            onExpandedChange={onExpandedChange}
          />

          <AudioElement
            ref={this.mediaElementRef}
            title={title}
            onCanPlay={this.handleCanPlay}
            onCanPlayThrough={this.handleCanPlayThrough}
            onEnded={this.handleEnded}
            onLoadedData={this.handleLoadedData}
            onLoadedMetaData={this.handleLoadedMetaData}
            onLoadStart={this.handleLoadStart}
            onDurationChange={this.handleDurationChange}
            onPause={this.handlePause}
            onPlaying={this.handlePlaying}
            onProgress={this.handleProgress}
            onTimeUpdate={this.handleTimeUpdate}
            onVolumeChange={this.handleVolumeChange}
            onWaiting={this.handleWaiting}
          >
            {children}
            {captions?.map(({ src, lang, isDefault }) => {
              return (
                <Track
                  kind="captions"
                  key={src}
                  src={src}
                  srcLang={lang}
                  isDefault={isDefault}
                  onCueChange={this.handleTrackCueChange}
                />
              );
            })}
          </AudioElement>
        </div>
        {this.props.isDesktopLayout && <AudioCaptions direction="down" />}
      </MediaPlayerContext.Provider>
    );
  }
}
